import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { reportSpamRequest, runMailMoves, moveErrorMessage } from '../utils/reportSpam';
import { moveMailRequest } from '../utils/moveMail';
import MoveToFolderButton from './MoveToFolderButton';
import { hasMailPassword, getMailPassword } from '../utils/mailCredentials';
import { getMailSettings } from '../utils/mailSettings';
import {
  buildThreadMessages,
  getConversationThreadMeta,
  getMailDedupeKey,
  getMailTimestamp,
  groupInboxByThread,
  extractEmailAddress,
  normalizeEmail,
  normalizeMessageId,
} from '../utils/mailThread';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import UserAvatar from './UserAvatar';
import { LabelBadge, LabelSelector, CreateLabelModal } from './labels';
import {
  assignLabelToMail,
  bulkAddLabel,
  bulkRemoveLabel,
  buildMailIdentity,
  fetchLabelAssignments,
  fetchLabels,
  resolveMailLabelKey,
  unassignLabelFromMail,
} from '../utils/labelApi';
import './Inbox.css';
import { enhanceHtmlLinks, linkifyPlainText } from '../utils/mailLinks';
import { CATEGORY_TABS, getMailCategory } from '../utils/mailCategory';
import AiAssistantPanel from './ai/AiAssistantPanel';
import MailWorkspaceActions from './ui/MailWorkspaceActions';

const Inbox = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSnoozedView = location.pathname === '/snoozed';
  const [snoozeMenu, setSnoozeMenu] = useState(null);
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showHeaderDetails, setShowHeaderDetails] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyTargetMail, setReplyTargetMail] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [showQuotedContent, setShowQuotedContent] = useState(false);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [expandedThreadIndex, setExpandedThreadIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mailToDelete, setMailToDelete] = useState(null);
  const [showDeleteReplyConfirm, setShowDeleteReplyConfirm] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [settings, setSettings] = useState(getMailSettings);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);
  const [activeCategory, setActiveCategory] = useState('primary');
  const [listPage, setListPage] = useState(0);
  const LIST_PAGE_SIZE = 50;
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const [messageMoreMenuKey, setMessageMoreMenuKey] = useState(null);
  const [detailMoreMenuOpen, setDetailMoreMenuOpen] = useState(false);
  const [messageReactions, setMessageReactions] = useState({});
  const [reactionPickerKey, setReactionPickerKey] = useState(null);
  const [senderProfiles, setSenderProfiles] = useState({});
  const [hoveredContactKey, setHoveredContactKey] = useState('');
  const [copiedEmail, setCopiedEmail] = useState('');
  const [userLabels, setUserLabels] = useState([]);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);
  const [labelMenuBusy, setLabelMenuBusy] = useState(false);
  const copiedResetTimer = useRef(null);
  const threadEndRef = useRef(null);
  const mailDetailScrollRef = useRef(null);

  useEffect(() => () => {
    if (copiedResetTimer.current) {
      clearTimeout(copiedResetTimer.current);
    }
  }, []);
  const senderProfileRequestsRef = useRef(new Set());
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🙏', '✅', '🔥'];
  const REACTIONS_STORAGE_KEY = 'mailMessageReactions';

  const isImapMail = (m) => m && (m.source === 'imap' || m.uid);
  const getMailUid = (mail) => mail?.uid ?? mail?.imapUid;
  const apiBase = process.env.REACT_APP_API_URL || '';

  const getAttachmentUrl = (mail, attachmentIndex, download = false) => {
    const partIndex = mail?.attachments?.[attachmentIndex]?.index ?? attachmentIndex;
    const downloadQuery = download ? '?download=1' : '';
    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      return `${apiBase}/api/mail/server/message/${uid}/attachment/${partIndex}${downloadQuery}`;
    }
    return `${apiBase}/api/mail/${mail._id}/attachment/${partIndex}${downloadQuery}`;
  };

  const getAttachmentAuthHeaders = () => {
    const headers = {};
    const token = localStorage.getItem('token');
    const mailPassword = getMailPassword();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (mailPassword) headers['x-mail-password'] = mailPassword;
    return headers;
  };

  const downloadAttachmentFile = async (mail, attachmentIndex, filename) => {
    const url = getAttachmentUrl(mail, attachmentIndex, true);
    const response = await fetch(url, { headers: getAttachmentAuthHeaders() });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(link);
  };
  const getLocalMailId = (mail) => mail?._id ?? mail?.id;
  const getMailKey = (mail) => String(getMailUid(mail) ?? getLocalMailId(mail) ?? mail?.messageId ?? '');
  const looksLikeHtmlBody = (value) => typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);
  const isEntityEncodedHtml = (value) => typeof value === 'string' && /^&lt;[a-z]/i.test(value.trim());
  const decodeCommonEntities = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  };
  const getMailHtmlContent = (mail) => {
    if (!mail) return '';
    const htmlField = mail.html && String(mail.html).trim() ? String(mail.html).trim() : '';
    const body = String(mail.body || '').trim();
    let raw = htmlField;
    if (!raw && looksLikeHtmlBody(body)) raw = body;
    if (!raw && isEntityEncodedHtml(body)) raw = decodeCommonEntities(body);
    if (!raw) return '';
    if (isEntityEncodedHtml(raw)) return decodeCommonEntities(raw);
    return enhanceHtmlLinks(raw);
  };
  const isMailRead = (mail) => Boolean(mail.seen ?? mail.isRead);

  const getPlainMailBody = (mail) => {
    const raw = String(mail?.text || mail?.body || mail?.preview || '').trim();
    if (!raw) return '';

    let content = raw;
    if (isEntityEncodedHtml(content)) content = decodeCommonEntities(content);
    if (looksLikeHtmlBody(content)) {
      try {
        const container = document.createElement('div');
        container.innerHTML = content;
        return (container.textContent || container.innerText || '')
          .replace(/\u00a0/g, ' ')
          .trim();
      } catch {
        return content
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/gi, ' ')
          .replace(/&amp;/g, '&')
          .trim();
      }
    }

    return content.replace(/&nbsp;/gi, ' ').replace(/&amp;/g, '&').trim();
  };

  const getMailPreview = (mail) => {
    if (mail.preview) return mail.preview;
    const textContent = getPlainMailBody(mail);
    return textContent.length > 100 ? `${textContent.substring(0, 100)}...` : textContent;
  };
  const formatAttachmentSize = (bytes) => {
    if (!bytes) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };
  const isImageAttachment = (attachment) => {
    if (attachment?.mimetype?.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(attachment?.filename || ''));
  };
  const getAttachmentCountLabel = (count) =>
    count === 1 ? 'One attachment' : `${count} attachments`;
  const truncateAttachmentName = (name, max = 44) => {
    const value = String(name || 'image');
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  };
  const getAttachmentChipType = (attachment) => {
    const mime = String(attachment?.mimetype || '').toLowerCase();
    const filename = String(attachment?.filename || '').toLowerCase();
    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(filename)) return 'image';
    if (mime.includes('pdf') || filename.endsWith('.pdf')) return 'pdf';
    if (
      mime.includes('word')
      || mime.includes('document')
      || mime.includes('msword')
      || /\.(doc|docx)$/.test(filename)
    ) return 'doc';
    if (mime.includes('sheet') || mime.includes('excel') || /\.(xls|xlsx|csv)$/.test(filename)) return 'sheet';
    return 'file';
  };
  const renderAttachmentChipIcon = (type) => {
    if (type === 'image') {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        </svg>
      );
    }
    if (type === 'doc') {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z" />
        </svg>
      );
    }
    if (type === 'sheet') {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM8 13h3v2H8v-2zm0 4h3v2H8v-2zm5-4h3v2h-3v-2zm0 4h3v2h-3v-2z" />
        </svg>
      );
    }
    if (type === 'pdf') {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM7 15h2v2H7v-2zm4 0h6v2h-6v-2zm-4-3h2v2H7v-2zm4 0h6v2h-6v-2z" />
        </svg>
      );
    }
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
      </svg>
    );
  };
  const openAttachmentFile = async (mail, attachmentIndex) => {
    const url = getAttachmentUrl(mail, attachmentIndex);
    const response = await fetch(url, { headers: getAttachmentAuthHeaders() });
    if (!response.ok) throw new Error('Open failed');
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
  };
  const readingPane = settings.readingPane || 'no-split';
  const isDesktop = !isMobileView;
  const useSplitLayout = isDesktop && readingPane !== 'no-split';
  const hasDetailOpen = Boolean(selectedMail) && useSplitLayout;
  const fullDetailView = Boolean(selectedMail) && isDesktop && readingPane === 'no-split';
  const hideMailList =
    (isMobileView && selectedMail) ||
    (isDesktop && readingPane === 'no-split' && selectedMail);
  const mobileSelectionActive = isMobileView && selectedMailIds.length > 0 && !selectedMail;
  const sortedMails = [...mails].sort((a, b) => {
    if (settings.inboxType === 'unread') {
      return Number(isMailRead(a)) - Number(isMailRead(b));
    }
    if (settings.inboxType === 'important' || settings.inboxType === 'priority') {
      return Number(Boolean(b.isImportant || b.isStarred)) - Number(Boolean(a.isImportant || b.isStarred));
    }
    if (settings.inboxType === 'starred') {
      return Number(Boolean(b.isStarred)) - Number(Boolean(a.isStarred));
    }
    return getMailTimestamp(b) - getMailTimestamp(a);
  });

  const showCategoryTabs = !isSnoozedView;
  const categoryUnreadCounts = mails.reduce((acc, mail) => {
    const cat = getMailCategory(mail);
    if (!isMailRead(mail)) acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const categoryTotals = mails.reduce((acc, mail) => {
    const cat = getMailCategory(mail);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const ALWAYS_VISIBLE_TABS = ['primary', 'social', 'promotions'];
  const visibleCategoryTabs = showCategoryTabs
    ? CATEGORY_TABS.filter(
        (tab) => ALWAYS_VISIBLE_TABS.includes(tab.id) || categoryTotals[tab.id] > 0
      )
    : [];
  const categoryFilteredMails = showCategoryTabs
    ? sortedMails.filter((mail) => getMailCategory(mail) === activeCategory)
    : sortedMails;

  const displayedMails = groupInboxByThread(categoryFilteredMails, currentUser?.email);

  const totalMailCount = displayedMails.length;
  const maxListPage = totalMailCount > 0 ? Math.ceil(totalMailCount / LIST_PAGE_SIZE) - 1 : 0;
  const safeListPage = Math.min(listPage, maxListPage);
  const pageMails = displayedMails.slice(
    safeListPage * LIST_PAGE_SIZE,
    safeListPage * LIST_PAGE_SIZE + LIST_PAGE_SIZE
  );
  const visibleStart = totalMailCount > 0 ? safeListPage * LIST_PAGE_SIZE + 1 : 0;
  const visibleEnd = totalMailCount > 0 ? Math.min(safeListPage * LIST_PAGE_SIZE + pageMails.length, totalMailCount) : 0;
  const canGoNewer = safeListPage > 0;
  const canGoOlder = safeListPage < maxListPage;

  useEffect(() => {
    setListPage(0);
  }, [mails.length, settings.inboxType, activeCategory]);

  useEffect(() => {
    if (currentUser) {
      fetchInbox();
      loadUserLabels();
      const interval = setInterval(() => { fetchInbox(false); }, 15000);
      const onLabelsUpdated = () => {
        loadUserLabels();
        fetchInbox(false);
      };
      window.addEventListener('labelsUpdated', onLabelsUpdated);
      return () => {
        clearInterval(interval);
        window.removeEventListener('labelsUpdated', onLabelsUpdated);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isSnoozedView]);

  useEffect(() => {
    if (!showLabelMenu) return undefined;
    const onPointerDown = (event) => {
      if (!event.target.closest('.toolbar-label-menu-wrap')) {
        setShowLabelMenu(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showLabelMenu]);

  useEffect(() => {
    const handleSettingsChanged = (event) => {
      setSettings(event.detail || getMailSettings());
    };

    window.addEventListener('mailSettingsChanged', handleSettingsChanged);
    return () => window.removeEventListener('mailSettingsChanged', handleSettingsChanged);
  }, []);

  // Clicking the sidebar folder (e.g. Inbox) while a mail is open should
  // return to the list view even though the route doesn't change.
  useEffect(() => {
    const handleResetView = () => {
      setSelectedMail(null);
      setSelectedMailIds([]);
      setShowHeaderDetails(false);
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);
    };

    window.addEventListener('resetMailView', handleResetView);
    return () => window.removeEventListener('resetMailView', handleResetView);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHeaderDetails && !event.target.closest('.recipient-info') && !event.target.closest('.header-details-popup')) {
        setShowHeaderDetails(false);
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);
      }
      if (messageMoreMenuKey && !event.target.closest('.gmail-msg-more-menu') && !event.target.closest('.msg-more-btn')) {
        setMessageMoreMenuKey(null);
      }
      if (detailMoreMenuOpen && !event.target.closest('.detail-more-menu') && !event.target.closest('.detail-more-btn')) {
        setDetailMoreMenuOpen(false);
      }
    };

    if (showHeaderDetails || messageMoreMenuKey || detailMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showHeaderDetails, messageMoreMenuKey, detailMoreMenuOpen]);

  const attachLabelsToMails = async (mailList = []) => {
    if (!mailList.length) return mailList;
    try {
      const identities = mailList.map((mail) => buildMailIdentity(mail));
      const response = await fetchLabelAssignments(identities);
      if (!response.success) return mailList;
      const map = response.data || {};
      return mailList.map((mail) => {
        const key = resolveMailLabelKey(mail);
        const labels =
          map[key] ||
          map[String(mail._id || '')] ||
          (mail.messageId ? map[`mid:${mail.messageId}`] : null) ||
          (mail.uid != null ? map[`uid:${mail.mailbox || 'INBOX'}:${mail.uid}`] : null) ||
          [];
        return { ...mail, labels };
      });
    } catch (error) {
      console.error('Error attaching labels:', error);
      return mailList;
    }
  };

  const loadUserLabels = async () => {
    try {
      const response = await fetchLabels(true);
      if (response.success) setUserLabels(response.data || []);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const fetchInbox = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');

    if (!hasMailPassword()) {
      if (showLoading) setLoading(false);
      setError('Mailbox login required. Sign in again with your email password.');
      setMails([]);
      return;
    }

    try {
      const endpoint = isSnoozedView ? '/api/mail/server/snoozed' : '/api/mail/inbox';
      const response = await axiosInstance.get(endpoint);
      if (response.data.success) {
        const enriched = await attachLabelsToMails(response.data.data || []);
        setMails(enriched);
        if (!isSnoozedView) window.dispatchEvent(new CustomEvent('inboxUpdated'));
      } else {
        setError(response.data.message || 'Failed to fetch inbox');
      }
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'MAILBOX_CREDENTIALS_REQUIRED' || code === 'MAILBOX_AUTH_FAILED') {
        setError(
          error.response?.data?.message ||
            'Mailbox login required. Sign in again with your email password.'
        );
        return;
      }
      console.error('Error fetching inbox:', error);
      setError(error.response?.data?.message || 'Error fetching inbox. Please try again.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadImapMessageDetail = async (mail) => {
    if (!isImapMail(mail) || mail.detailsLoaded) return mail;
    const uid = getMailUid(mail);
    if (!uid) return mail;

    try {
      const response = await axiosInstance.get(`/api/mail/server/message/${uid}`);
      if (response.data.success) {
        return {
          ...mail,
          ...response.data.data,
          // Keep personal labels — IMAP payload must not wipe them.
          labels: Array.isArray(mail.labels) ? mail.labels : [],
          isStarred: Boolean(response.data.data.isStarred),
          detailsLoaded: true,
        };
      }
    } catch (error) {
      console.error('Error loading IMAP message detail:', error);
    }
    return mail;
  };

  const mergeMailLabels = (...mailItems) => {
    const byId = new Map();
    mailItems.forEach((item) => {
      (item?.labels || []).forEach((label) => {
        if (label?._id) byId.set(String(label._id), label);
      });
    });
    return Array.from(byId.values());
  };

  const ensureMailLabels = async (mail, extras = []) => {
    if (!mail) return mail;
    const existing = Array.isArray(mail.labels) ? mail.labels : [];
    if (existing.length) {
      return { ...mail, labels: mergeMailLabels(mail, ...extras) };
    }
    const [enriched] = await attachLabelsToMails([mail]);
    return {
      ...mail,
      ...(enriched || {}),
      labels: mergeMailLabels(enriched, mail, ...extras),
    };
  };

  const loadConversationForMail = async (mail, inboxList = mails) => {
    let sentMails = [];
    try {
      const sentResponse = await axiosInstance.get('/api/mail/sent');
      if (sentResponse.data.success) {
        sentMails = sentResponse.data.data || [];
      }
    } catch (err) {
      console.error('Error loading sent mails for thread:', err);
    }

    const preliminaryThread = buildThreadMessages(mail, inboxList, sentMails, currentUser?.email);
    const threadKeys = new Set(preliminaryThread.map((item) => getMailDedupeKey(item)));

    const enrichedInbox = await Promise.all(
      inboxList.map(async (item) => {
        if (!threadKeys.has(getMailDedupeKey(item))) return item;
        return loadImapMessageDetail(item);
      })
    );

    const enrichedSent = await Promise.all(
      sentMails.map(async (item) => {
        if (!threadKeys.has(getMailDedupeKey(item))) return item;
        return loadImapMessageDetail(item);
      })
    );

    const rootMail = isImapMail(mail) ? await loadImapMessageDetail(mail) : mail;
    return buildThreadMessages(rootMail, enrichedInbox, enrichedSent, currentUser?.email);
  };

  const getReplyTargetMail = () => {
    if (replyTargetMail) return replyTargetMail;

    const me = normalizeEmail(currentUser?.email);
    for (let i = conversationMessages.length - 1; i >= 0; i -= 1) {
      const msg = conversationMessages[i];
      if (normalizeEmail(msg.from) !== me) return msg;
    }
    return selectedMail;
  };

  const getReplyRecipientEmail = (mail = getReplyTargetMail()) => {
    if (!mail) return '';
    return extractSenderEmail(mail.from) || String(mail.from || '').trim().toLowerCase();
  };

  const resolveReplyTargetMail = (mail) => {
    if (mail) return mail;

    const me = normalizeEmail(currentUser?.email);
    for (let i = conversationMessages.length - 1; i >= 0; i -= 1) {
      const msg = conversationMessages[i];
      if (normalizeEmail(msg.from) !== me) return msg;
    }
    return selectedMail;
  };

  const isOwnConversationMessage = (mail) =>
    extractEmailAddress(mail?.from) === normalizeEmail(currentUser?.email);

  const handleMailClick = async (mail) => {
    setConversationMessages([]);
    setShowReplyBox(false);
    setReplyTargetMail(null);
    setShowQuotedContent(false);
    setShowHeaderDetails(false);
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);

    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      if (!uid) {
        setAlertMessage('Could not open message');
        setShowAlert(true);
        return;
      }

      try {
        const labeledRoot = await ensureMailLabels(mail);
        const thread = await loadConversationForMail(labeledRoot);
        const latest = thread[thread.length - 1] || labeledRoot;
        const labels = mergeMailLabels(labeledRoot, latest, ...thread);
        setConversationMessages(thread);
        setSelectedMail({ ...latest, labels });
        if (!isMailRead(latest)) {
          try {
            await axiosInstance.patch(`/api/mail/server/read/${uid}`);
            setMails((prev) => prev.map((x) => (getMailUid(x) === uid ? { ...x, seen: true, isRead: true } : x)));
          } catch (_e) { /* best-effort */ }
        }
      } catch (error) {
        console.error('Error loading IMAP message:', error);
        setAlertMessage('Could not open message');
        setShowAlert(true);
      }
      return;
    }

    const labeledRoot = await ensureMailLabels(mail);
    const thread = await loadConversationForMail(labeledRoot);
    const latest = thread[thread.length - 1] || labeledRoot;
    const labels = mergeMailLabels(labeledRoot, latest, ...thread);
    setConversationMessages(thread);
    setSelectedMail({ ...latest, labels });

    if (!isMailRead(mail)) {
      try {
        await axiosInstance.patch(`/api/mail/mark-read/${mail._id}`);
        setMails(mails.map((m) => (m._id === mail._id ? { ...m, isRead: true } : m)));
        setSelectedMail((prev) => ({ ...(prev || latest), isRead: true, labels }));
      } catch (error) {
        console.error('Error marking mail as read:', error);
      }
    }
  };

  const handleExpandThreadMessage = async (msg, index) => {
    setExpandedThreadIndex(index);

    if (!isImapMail(msg) || msg.detailsLoaded) return;
    const uid = getMailUid(msg);
    if (!uid) return;

    try {
      const detail = await axiosInstance.get(`/api/mail/server/message/${uid}`);
      if (!detail.data.success) return;
      const detailed = {
        ...detail.data.data,
        isStarred: Boolean(detail.data.data.isStarred),
        detailsLoaded: true,
      };
      setConversationMessages((prev) =>
        prev.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                ...detailed,
                labels: Array.isArray(item.labels) ? item.labels : [],
                body: detailed.body || item.body,
                text: detailed.text || item.text,
                html: detailed.html || item.html,
                attachments: detailed.attachments?.length ? detailed.attachments : item.attachments,
              }
            : item
        )
      );
    } catch (error) {
      console.error('Error loading thread message details:', error);
    }
  };

  const handleToggleStar = async (e, mail) => {
    e.stopPropagation();
    const mailKey = getMailKey(mail);
    const nextStarred = !mail.isStarred;

    const applyStarState = (isStarred) => {
      setMails((prev) => prev.map((m) => (getMailKey(m) === mailKey ? { ...m, isStarred } : m)));
      setSelectedMail((prev) =>
        prev && getMailKey(prev) === mailKey ? { ...prev, isStarred } : prev
      );
      setConversationMessages((prev) =>
        prev.map((m) => (getMailKey(m) === mailKey ? { ...m, isStarred } : m))
      );
    };

    applyStarState(nextStarred);

    try {
      let response;
      if (isImapMail(mail)) {
        const uid = getMailUid(mail);
        if (!uid) {
          applyStarState(!nextStarred);
          return;
        }
        response = await axiosInstance.patch(`/api/mail/server/star/${uid}`, { starred: nextStarred });
      } else {
        response = await axiosInstance.patch(`/api/mail/star/${mail._id}`);
      }

      if (response.data.success) {
        const isStarred = response.data.data?.isStarred ?? nextStarred;
        applyStarState(isStarred);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      } else {
        applyStarState(!nextStarred);
      }
    } catch (error) {
      console.error('Error toggling star:', error);
      applyStarState(!nextStarred);
      setAlertMessage(error.response?.data?.message || 'Could not update star');
      setShowAlert(true);
    }
  };

  const handleCloseMail = () => {
    setSelectedMail(null);
    setConversationMessages([]);
    setExpandedThreadIndex(null);
    setShowHeaderDetails(false);
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);
    setShowReplyBox(false);
    setReplyTargetMail(null);
    setReplyBody('');
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(REACTIONS_STORAGE_KEY);
      if (stored) setMessageReactions(JSON.parse(stored) || {});
    } catch (err) {
      console.error('Could not load reactions:', err);
    }
  }, []);

  useEffect(() => {
    if (!reactionPickerKey) return undefined;
    const closePicker = () => setReactionPickerKey(null);
    document.addEventListener('click', closePicker);
    return () => document.removeEventListener('click', closePicker);
  }, [reactionPickerKey]);

  useEffect(() => {
    if (conversationMessages.length > 0) {
      setExpandedThreadIndex(conversationMessages.length - 1);
    } else {
      setExpandedThreadIndex(null);
    }
  }, [conversationMessages]);

  const getRecipientLabel = (msg, isOwn) => {
    if (isOwn) {
      const to = String(msg?.to || '').split(',')[0]?.trim();
      return to ? `to ${parseSenderDisplay(to)}` : 'to recipient';
    }
    return 'to me';
  };

  const renderHeaderDetailsPopup = (msg) => {
    const fromEmail = extractSenderEmail(msg?.from);
    const senderDomain = fromEmail.split('@')[1] || '';
    const toValue = msg.to || currentUser?.email || 'Me';
    const renderAddress = (rawValue, fallbackEmail = '') => {
      const value = String(rawValue || '').trim();
      const email = extractSenderEmail(value) || fallbackEmail;
      const name = value ? parseSenderDisplay(value) : email;
      if (!email) return name || 'Unknown';
      return (
        <>
          {name && name !== email && <strong>{name}</strong>}
          {name && name !== email ? ' ' : ''}
          <span className="popup-email">&lt;{email}&gt;</span>
        </>
      );
    };
    const renderAddressList = (rawValue, fallbackEmail = '') => {
      const value = String(rawValue || '').trim();
      if (!value) return renderAddress('', fallbackEmail);
      const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
      if (parts.length <= 1) return renderAddress(value, fallbackEmail);
      return parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 && ', '}
          {renderAddress(part)}
        </React.Fragment>
      ));
    };

    return (
      <div className="header-details-popup gmail-thread-details-popup" role="dialog" aria-label="Message details">
        <div className="popup-content">
          <div className="popup-row">
            <span className="popup-label">from:</span>
            <span className="popup-value">{renderAddress(msg.from, fromEmail)}</span>
          </div>
          <div className="popup-row">
            <span className="popup-label">to:</span>
            <span className="popup-value popup-recipients">{renderAddressList(toValue)}</span>
          </div>
          <div className="popup-row">
            <span className="popup-label">date:</span>
            <span className="popup-value">{formatFullDate(msg.createdAt || msg.date)}</span>
          </div>
          <div className="popup-row">
            <span className="popup-label">subject:</span>
            <span className="popup-value">{msg.subject || '(no subject)'}</span>
          </div>
          {msg.cc && (
            <div className="popup-row">
              <span className="popup-label">cc:</span>
              <span className="popup-value popup-recipients">{Array.isArray(msg.cc) ? msg.cc.join(', ') : msg.cc}</span>
            </div>
          )}
          {msg.bcc && (
            <div className="popup-row">
              <span className="popup-label">bcc:</span>
              <span className="popup-value popup-recipients">{Array.isArray(msg.bcc) ? msg.bcc.join(', ') : msg.bcc}</span>
            </div>
          )}
          <div className="popup-row">
            <span className="popup-label">mailed-by:</span>
            <span className="popup-value">{senderDomain || 'atozasmail.in'}</span>
          </div>
          <div className="popup-row">
            <span className="popup-label">signed-by:</span>
            <span className="popup-value">{senderDomain || 'atozasmail.in'}</span>
          </div>
          <div className="popup-row security">
            <span className="popup-label">security:</span>
            <span className="popup-value security-text">
              <svg className="popup-lock" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17 8h-1V6c0-2.76-2.24-5-5-5S6 3.24 6 6v2H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H8V6zm4 11.73V19h-2v-1.27c-.6-.35-1-1-1-1.73 0-1.1.9-2 2-2s2 .9 2 2c0 .73-.4 1.38-1 1.73z" />
              </svg>
              Standard encryption (TLS)
              <span className="popup-learn-more"> Learn more</span>
            </span>
          </div>
          <div className="popup-row importance">
            <span className="popup-label">
              <span className="importance-marker" aria-hidden="true" />:
            </span>
            <span className="popup-value">Important according to ATOZASMail.</span>
          </div>
        </div>
      </div>
    );
  };

  const handleReply = (mail) => {
    const target = resolveReplyTargetMail(mail);
    if (!target) {
      setAlertMessage('Could not determine who to reply to');
      setShowAlert(true);
      return;
    }

    setReplyTargetMail(target);
    setShowReplyBox(true);
    setReplyBody('');
    setShowQuotedContent(true);

    setTimeout(() => {
      threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  const handleReplyAll = (mail) => {
    handleReply(mail);
  };

  const formatGmailQuoteHeader = (mail) => {
    if (!mail) return '';
    const date = new Date(mail.createdAt || mail.date);
    const dateStr = date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const fromRaw = String(mail.from || '').trim();
    const name = parseSenderDisplay(fromRaw);
    const email = extractSenderEmail(fromRaw);
    const fromPart = email && name && name !== email ? `${name} <${email}>` : fromRaw;
    return `On ${dateStr} ${fromPart} wrote:`;
  };

  const getQuotedContent = (mail = getReplyTargetMail()) => {
    if (!mail) return { header: '', body: '' };

    const plainBody = getPlainMailBody(mail);
    const nestedQuoteIndex = plainBody.search(/\n\nOn .+ wrote:\s*/i);
    const bodyToQuote = nestedQuoteIndex > 0
      ? plainBody.slice(0, nestedQuoteIndex).trim()
      : plainBody;

    return {
      header: formatGmailQuoteHeader(mail),
      body: bodyToQuote || plainBody,
    };
  };

  const splitMessageForDisplay = (msg) => {
    const raw = getPlainMailBody(msg);
    if (!raw) {
      return { replyPart: '', quoteHeader: '', quoteBody: '', hasQuote: false };
    }

    const markerMatch = raw.match(/(\n\n|\n)(On .+ wrote:)\s*/i);
    if (markerMatch) {
      const markerIndex = raw.indexOf(markerMatch[2]);
      return {
        replyPart: raw.slice(0, markerIndex).trim() || msg.replyText || '',
        quoteHeader: markerMatch[2].trim(),
        quoteBody: raw.slice(markerIndex + markerMatch[2].length).trim(),
        hasQuote: true,
      };
    }

    return {
      replyPart: msg.replyText || raw,
      quoteHeader: '',
      quoteBody: '',
      hasQuote: false,
    };
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      setAlertMessage('Please enter a reply message');
      setShowAlert(true);
      return;
    }

    const targetMail = getReplyTargetMail();
    if (!targetMail) {
      setAlertMessage('Could not determine reply recipient');
      setShowAlert(true);
      return;
    }

    const recipientEmail = getReplyRecipientEmail(targetMail);
    if (!recipientEmail) {
      setAlertMessage('Could not determine reply recipient email');
      setShowAlert(true);
      return;
    }

    setIsSendingReply(true);
    try {
      const { header, body: quotedBody } = getQuotedContent(targetMail);
      const fullBody = `${replyBody.trim()}\n\n${header}\n${quotedBody}`;
      const replySubject = targetMail.subject?.startsWith('Re:')
        ? targetMail.subject
        : `Re: ${targetMail.subject || ''}`;
      const threadMeta = getConversationThreadMeta(conversationMessages.length ? conversationMessages : [selectedMail]);
      const parentMessageId = normalizeMessageId(targetMail?.messageId);
      const inReplyTo = parentMessageId ? `<${parentMessageId}>` : threadMeta.inReplyTo
        ? (threadMeta.inReplyTo.includes('<') ? threadMeta.inReplyTo : `<${threadMeta.inReplyTo}>`)
        : undefined;

      const response = await axiosInstance.post('/api/mail/send', {
        to: recipientEmail,
        subject: replySubject,
        body: fullBody,
        threadId: threadMeta.threadId || undefined,
        inReplyTo,
        references: threadMeta.references || undefined,
      });

      if (response.data.success) {
        const sentReply = {
          _id: response.data.data?.sentMailId || `temp-${Date.now()}`,
          from: currentUser?.email,
          to: recipientEmail,
          subject: replySubject,
          body: fullBody,
          replyText: replyBody.trim(),
          threadId: response.data.data?.threadId || threadMeta.threadId || '',
          messageId: normalizeMessageId(response.data.data?.messageId || ''),
          inReplyTo: parentMessageId,
          createdAt: new Date().toISOString(),
          folder: 'sent',
        };

        setConversationMessages((prev) => {
          const next = [...prev, sentReply];
          setExpandedThreadIndex(next.length - 1);
          return next;
        });
        setSelectedMail((prev) => ({ ...(prev || targetMail), subject: replySubject }));

        setShowReplyBox(false);
        setReplyBody('');
        setReplyTargetMail(null);
        setShowQuotedContent(false);

        setTimeout(() => {
          threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
        fetchInbox(false);
      } else {
        setAlertMessage(response.data.message || 'Failed to send reply');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      setAlertMessage(error.response?.data?.message || 'Error sending reply. Please try again.');
      setShowAlert(true);
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleCancelReply = () => {
    setShowReplyBox(false);
    setReplyBody('');
    setReplyTargetMail(null);
  };

  const handleDeleteReply = async (replyId, e) => {
    e.stopPropagation();
    setReplyToDelete(replyId);
    setShowDeleteReplyConfirm(true);
  };

  const confirmDeleteReply = async () => {
    if (!replyToDelete) return;

    const deletingReplyId = replyToDelete;
    setShowDeleteReplyConfirm(false);

    try {
      const response = await axiosInstance.patch(`/api/mail/move-to-trash/${deletingReplyId}`);
      if (response.data.success) {
        // Remove reply from display
        setConversationMessages((prev) => prev.filter((reply) => reply._id !== deletingReplyId));
        setAlertMessage('Reply deleted successfully');
        setShowAlert(true);
      } else {
        setAlertMessage(response.data.message || 'Failed to delete reply');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      setAlertMessage(error.response?.data?.message || 'Error deleting reply. Please try again.');
      setShowAlert(true);
    } finally {
      setReplyToDelete(null);
    }
  };

  const getForwardTargetMail = () => {
    if (expandedThreadIndex !== null && conversationMessages[expandedThreadIndex]) {
      return conversationMessages[expandedThreadIndex];
    }
    return getReplyTargetMail();
  };

  const handleForward = (mail) => {
    const targetMail = mail || getForwardTargetMail();
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);

    if (!targetMail) {
      setAlertMessage('Unable to forward this message');
      setShowAlert(true);
      return;
    }

    if (isImapMail(targetMail)) {
      const uid = getMailUid(targetMail);
      if (!uid) {
        setAlertMessage('Unable to forward this message');
        setShowAlert(true);
        return;
      }
      navigate(`/compose?forwardUid=${encodeURIComponent(uid)}`);
      return;
    }

    const mailId = getLocalMailId(targetMail);
    if (!mailId) {
      setAlertMessage('Unable to forward this message');
      setShowAlert(true);
      return;
    }
    navigate(`/compose?forward=${encodeURIComponent(mailId)}`);
  };

  const toggleMessageMoreMenu = (event, msg) => {
    event.stopPropagation();
    const key = getMailKey(msg);
    setMessageMoreMenuKey((current) => (current === key ? null : key));
    setDetailMoreMenuOpen(false);
  };

  const runMessageMoreAction = (event, action) => {
    event.stopPropagation();
    action();
    setMessageMoreMenuKey(null);
    setDetailMoreMenuOpen(false);
  };

  const renderMessageMoreMenu = (msg) => {
    const menuItems = (
      <>
        <button type="button" role="menuitem" onClick={(e) => runMessageMoreAction(e, () => handleForward(msg))}>
          Forward
        </button>
        <button type="button" role="menuitem" onClick={(e) => runMessageMoreAction(e, () => handleReplyAll(msg))}>
          Reply all
        </button>
        <button type="button" role="menuitem" onClick={(e) => runMessageMoreAction(e, () => handleMarkUnread(e, msg))}>
          {isMailRead(msg) ? 'Mark as unread' : 'Mark as read'}
        </button>
        {!isSnoozedView && (
          <button type="button" role="menuitem" onClick={(e) => runMessageMoreAction(e, () => handleSnoozeMail(e, msg))}>
            Snooze
          </button>
        )}
        <button type="button" role="menuitem" onClick={(e) => runMessageMoreAction(e, () => handleDeleteMail(e, getMailKey(msg)))}>
          Delete
        </button>
      </>
    );

    if (isMobileView) {
      return (
        <>
          <div className="msg-more-menu-backdrop" onClick={() => { setMessageMoreMenuKey(null); setDetailMoreMenuOpen(false); }} />
          <div className="gmail-msg-more-menu gmail-msg-more-sheet" onClick={(e) => e.stopPropagation()} role="menu">
            {menuItems}
          </div>
        </>
      );
    }

    return (
      <div className="gmail-msg-more-menu" onClick={(e) => e.stopPropagation()} role="menu">
        {menuItems}
      </div>
    );
  };

  const persistReactions = (next) => {
    setMessageReactions(next);
    try {
      localStorage.setItem(REACTIONS_STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      console.error('Could not save reactions:', err);
    }
  };

  const getMessageReactions = (mailKey) => messageReactions[mailKey] || [];

  const toggleMessageReaction = (mailKey, emoji) => {
    if (!mailKey || !emoji) return;
    const current = messageReactions[mailKey] || [];
    const exists = current.includes(emoji);
    const updated = exists
      ? current.filter((e) => e !== emoji)
      : [...current, emoji];
    const next = { ...messageReactions };
    if (updated.length > 0) {
      next[mailKey] = updated;
    } else {
      delete next[mailKey];
    }
    persistReactions(next);
  };

  const handleAddReaction = (mailKey) => {
    const key = typeof mailKey === 'string' ? mailKey : getMailKey(selectedMail);
    if (!key) return;
    setReactionPickerKey((prev) => (prev === key ? null : key));
  };

  const handlePickReaction = (mailKey, emoji) => {
    toggleMessageReaction(mailKey, emoji);
    setReactionPickerKey(null);
  };

  const parseSenderDisplay = (from) => {
    const raw = String(from || '').trim();
    if (!raw) return 'Unknown';

    const fromEmail = extractEmailAddress(from);
    const me = normalizeEmail(currentUser?.email);
    if (fromEmail && me && fromEmail === me) return 'me';

    const match = raw.match(/^(?:"([^"]+)"|([^<]+?))\s*<([^>]+)>$/);
    if (match) {
      const name = (match[1] || match[2] || '').trim();
      if (name) return name;
      return match[3].split('@')[0];
    }
    if (raw.includes('@')) {
      const name = raw.split('@')[0];
      return name.charAt(0).toUpperCase() + name.slice(1).replace(/[._-]/g, ' ');
    }
    return raw;
  };

  const extractSenderEmail = (from) => {
    const raw = decodeCommonEntities(String(from || '').trim());
    const angleMatch = raw.match(/<([^>]+)>/);
    if (angleMatch) return angleMatch[1].trim().toLowerCase();
    const emailMatch = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return emailMatch ? emailMatch[0].toLowerCase() : '';
  };

  const fetchSenderProfile = async (mail) => {
    const email = extractSenderEmail(mail?.from);
    if (!email || senderProfileRequestsRef.current.has(email)) return;

    senderProfileRequestsRef.current.add(email);
    try {
      const response = await axiosInstance.get('/api/users/suggestions', {
        params: { q: email },
      });
      const exactUser = response.data.success
        ? (response.data.data || []).find((user) => normalizeEmail(user.email) === email)
        : null;
      setSenderProfiles((prev) => ({
        ...prev,
        [email]: exactUser || null,
      }));
    } catch (error) {
      console.error('Error fetching sender profile:', error);
      setSenderProfiles((prev) => ({
        ...prev,
        [email]: null,
      }));
    }
  };

  const getSenderContact = (mail) => {
    const email = extractSenderEmail(mail?.from);
    const cachedProfile = email ? senderProfiles[email] : null;
    return {
      username: cachedProfile?.username || mail?.fromName || parseSenderDisplay(mail?.from),
      email,
      profileImage: cachedProfile?.profileImage || mail?.profileImage || mail?.senderProfileImage || '',
    };
  };

  const handleContactSendMail = (event, email) => {
    event.stopPropagation();
    if (!email) return;
    navigate(`/compose?to=${encodeURIComponent(email)}`);
  };

  const handleContactCopyEmail = async (event, email) => {
    event.stopPropagation();
    if (!email) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = email;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      // Show "Copied" feedback every time the button is clicked.
      setCopiedEmail(email);
      if (copiedResetTimer.current) {
        clearTimeout(copiedResetTimer.current);
      }
      copiedResetTimer.current = setTimeout(() => setCopiedEmail(''), 1500);
    } catch (error) {
      console.error('Error copying sender email:', error);
    }
  };

  const renderSenderHoverCard = (mail) => {
    const contact = getSenderContact(mail);
    const displayEmail = contact.email || String(mail?.from || '').trim();
    return (
      <span className="mail-contact-hover-card" role="dialog" aria-label={`${contact.username} contact card`} onClick={(event) => event.stopPropagation()}>
        <span className="mail-contact-card-top">
          <UserAvatar user={contact} size="lg" className="mail-contact-card-avatar" />
          <span className="mail-contact-card-details">
            <span className="mail-contact-card-name">{contact.username}</span>
            <span className="mail-contact-email-row">
              <span className="mail-contact-card-email">{displayEmail}</span>
              {(() => {
                const isCopied = copiedEmail && copiedEmail === displayEmail;
                return (
                  <button
                    type="button"
                    className={`mail-contact-copy-btn${isCopied ? ' copied' : ''}`}
                    onClick={(event) => handleContactCopyEmail(event, displayEmail)}
                    aria-label={isCopied ? 'Copied' : 'Copy email'}
                    title={isCopied ? 'Copied' : 'Copy email'}
                  >
                    {isCopied ? (
                      <>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                        <span className="mail-contact-copied-label">Copied</span>
                      </>
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                      </svg>
                    )}
                  </button>
                );
              })()}
            </span>
          </span>
        </span>
        <span className="mail-contact-card-actions">
          <button type="button" className="mail-contact-primary-action" onClick={(event) => handleContactSendMail(event, contact.email)} disabled={!contact.email}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
            Send Mail
          </button>
        </span>
      </span>
    );
  };

  const getSenderName = (email) => parseSenderDisplay(email);

  const getSenderInitial = (email) => {
    if (!email) return '?';
    const name = parseSenderDisplay(email);
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (email) => {
    const palettes = [
      { bg: '#e8f0fe', color: '#1967d2' },
      { bg: '#fce8e6', color: '#c5221f' },
      { bg: '#e6f4ea', color: '#137333' },
      { bg: '#fef7e0', color: '#b06000' },
      { bg: '#f3e8fd', color: '#9334e6' },
      { bg: '#e8eaed', color: '#5f6368' },
    ];
    const str = String(email || '');
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palettes[Math.abs(hash) % palettes.length];
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startLongPress = (mailKey) => {
    if (!isMobileView) return;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      if (navigator.vibrate) navigator.vibrate(30);
      setSelectedMailIds((prev) => (prev.includes(mailKey) ? prev : [...prev, mailKey]));
    }, 450);
  };

  const toggleMailSelection = (mailKey) => {
    setSelectedMailIds((prev) =>
      prev.includes(mailKey) ? prev.filter((id) => id !== mailKey) : [...prev, mailKey]
    );
  };

  const handleMailRowClick = (mail) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    const mailKey = getMailKey(mail);
    if (mobileSelectionActive || (isMobileView && selectedMailIds.length > 0)) {
      toggleMailSelection(mailKey);
      return;
    }
    handleMailClick(mail);
  };

  const handleMobileSelectAll = () => {
    const pageKeys = pageMails.map(getMailKey);
    const allPageSelected = pageKeys.every((key) => selectedMailIds.includes(key));
    if (allPageSelected) {
      setSelectedMailIds((prev) => prev.filter((id) => !pageKeys.includes(id)));
    } else {
      setSelectedMailIds((prev) => [...new Set([...prev, ...pageKeys])]);
    }
  };

  /** Archive one message, whichever store it lives in. */
  const archiveRequest = (mail) => {
    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      if (!uid) return Promise.reject(new Error('Missing IMAP message uid'));
      return axiosInstance.patch(`/api/mail/archive/${uid}`);
    }
    return axiosInstance.patch(`/api/mail/archive/${getLocalMailId(mail)}`);
  };

  const handleBulkArchive = async () => {
    if (selectedMailIds.length === 0) return;
    try {
      const selectedMails = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
      await Promise.all(selectedMails.map(archiveRequest));
      setMails(mails.filter((m) => !selectedMailIds.includes(getMailKey(m))));
      if (selectedMail && selectedMailIds.includes(getMailKey(selectedMail))) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error archiving mails:', error);
      setAlertMessage('Error archiving mails. Please try again.');
      setShowAlert(true);
    }
  };

  const handleArchiveMail = async (e, mail) => {
    e.stopPropagation();
    try {
      await archiveRequest(mail);
      const mailKey = getMailKey(mail);
      setMails(mails.filter((m) => getMailKey(m) !== mailKey));
      if (selectedMail && getMailKey(selectedMail) === mailKey) {
        setSelectedMail(null);
      }
      setSelectedMailIds((prev) => prev.filter((id) => id !== mailKey));
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error archiving mail:', error);
      setAlertMessage('Error archiving mail. Please try again.');
      setShowAlert(true);
    }
  };

  const handleDeleteMail = async (e, mailId) => {
    e.stopPropagation();
    setMailToDelete(mailId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMail = async () => {
    if (!mailToDelete) return;

    const deletingId = String(mailToDelete);
    setShowDeleteConfirm(false);

    // Exact one mail only — never delete by category or thread membership.
    const targetMail = mails.find((mail) => getMailKey(mail) === deletingId);
    if (!targetMail) {
      setMailToDelete(null);
      return;
    }

    try {
      let response;
      if (isImapMail(targetMail)) {
        const uid = getMailUid(targetMail);
        if (!uid) throw new Error('Missing IMAP message uid');
        response = await axiosInstance.patch(`/api/mail/trash/${uid}`);
      } else {
        const localId = getLocalMailId(targetMail);
        if (!localId) throw new Error('Missing message id');
        response = await axiosInstance.patch(`/api/mail/move-to-trash/${localId}`);
      }

      if (response.data.success) {
        setMails((prev) => prev.filter((m) => getMailKey(m) !== deletingId));
        setSelectedMailIds((prev) => prev.filter((id) => id !== deletingId));
        if (selectedMail && getMailKey(selectedMail) === deletingId) {
          setSelectedMail(null);
        }
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      } else {
        setAlertMessage(response.data.message || 'Failed to delete mail');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error deleting mail:', error);
      setAlertMessage(error.response?.data?.message || 'Error deleting mail. Please try again.');
      setShowAlert(true);
    } finally {
      setMailToDelete(null);
    }
  };

  const handleDownloadMail = (e, mail) => {
    e.stopPropagation();
    const mailContent = `From: ${mail.from}\nTo: ${mail.to}\nSubject: ${mail.subject}\nDate: ${formatFullDate(mail.createdAt || mail.date)}\n\n${mail.body || mail.preview || ''}`;
    const blob = new Blob([mailContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mail.subject || 'mail'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleMarkUnread = async (e, mail) => {
    e.stopPropagation();
    const mailKey = getMailKey(mail);
    if (isMailRead(mail)) {
      setMails(mails.map(m => getMailKey(m) === mailKey ? { ...m, seen: false, isRead: false } : m));
      if (selectedMail && getMailKey(selectedMail) === mailKey) {
        setSelectedMail({ ...selectedMail, seen: false, isRead: false });
      }
    } else {
      // Mark as read
      try {
        if (isImapMail(mail)) {
          const uid = getMailUid(mail);
          if (!uid) throw new Error('Missing IMAP message uid');
          await axiosInstance.patch(`/api/mail/server/read/${uid}`);
        } else {
          const mailId = getLocalMailId(mail);
          if (!mailId) throw new Error('Missing message id');
          await axiosInstance.patch(`/api/mail/mark-read/${mailId}`);
        }
        setMails(mails.map(m => getMailKey(m) === mailKey ? { ...m, seen: true, isRead: true } : m));
        if (selectedMail && getMailKey(selectedMail) === mailKey) {
          setSelectedMail({ ...selectedMail, seen: true, isRead: true });
        }
      } catch (error) {
        console.error('Error marking mail as read:', error);
      }
    }
  };

  const buildSnoozePresets = () => {
    const now = new Date();

    const laterToday = new Date(now);
    laterToday.setHours(now.getHours() + 3, 0, 0, 0);

    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);

    const weekend = new Date(now);
    const daysUntilSat = (6 - now.getDay() + 7) % 7 || 7;
    weekend.setDate(now.getDate() + daysUntilSat);
    weekend.setHours(8, 0, 0, 0);

    const nextWeek = new Date(now);
    const daysUntilMon = (1 - now.getDay() + 7) % 7 || 7;
    nextWeek.setDate(now.getDate() + daysUntilMon);
    nextWeek.setHours(8, 0, 0, 0);

    return [
      { label: 'Later today', sub: laterToday.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), date: laterToday },
      { label: 'Tomorrow', sub: tomorrow.toLocaleDateString([], { weekday: 'short' }) + ' 8:00 AM', date: tomorrow },
      { label: 'This weekend', sub: weekend.toLocaleDateString([], { weekday: 'short' }) + ' 8:00 AM', date: weekend },
      { label: 'Next week', sub: nextWeek.toLocaleDateString([], { weekday: 'short' }) + ' 8:00 AM', date: nextWeek },
    ];
  };

  const handleSnoozeMail = async (e, mail) => {
    e.stopPropagation();

    if (isSnoozedView) {
      // In the snoozed view, this button moves the mail back to the inbox.
      await performUnsnooze(mail);
      return;
    }

    if (!isImapMail(mail)) {
      setAlertMessage('Snooze is available for inbox mail only.');
      setShowAlert(true);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setSnoozeMenu({
      mail,
      x: Math.min(rect.left, window.innerWidth - 240),
      y: rect.bottom + 4,
    });
  };

  const performSnooze = async (mail, when) => {
    setSnoozeMenu(null);
    const uid = getMailUid(mail);
    if (!uid) return;

    const key = getMailKey(mail);
    setMails((prev) => prev.filter((m) => getMailKey(m) !== key));
    if (selectedMail && getMailKey(selectedMail) === key) setSelectedMail(null);

    try {
      await axiosInstance.post(`/api/mail/server/snooze/${uid}`, { snoozeUntil: when.toISOString() });
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error snoozing mail:', error);
      setAlertMessage(error.response?.data?.message || 'Failed to snooze. Please try again.');
      setShowAlert(true);
      fetchInbox(false);
    }
  };

  const performUnsnooze = async (mail) => {
    if (!mail?.messageId) {
      setAlertMessage('Cannot move this message back automatically.');
      setShowAlert(true);
      return;
    }
    const key = getMailKey(mail);
    setMails((prev) => prev.filter((m) => getMailKey(m) !== key));
    try {
      await axiosInstance.post('/api/mail/server/unsnooze', { messageId: mail.messageId });
    } catch (error) {
      console.error('Error unsnoozing mail:', error);
      setAlertMessage(error.response?.data?.message || 'Failed to move back to inbox.');
      setShowAlert(true);
      fetchInbox(false);
    }
  };

  // Select only mails visible in the current category tab (never other tabs).
  const handleSelectAll = () => {
    const visibleKeys = displayedMails.map(getMailKey).filter(Boolean);
    const allVisibleSelected =
      visibleKeys.length > 0 && visibleKeys.every((key) => selectedMailIds.includes(key));
    if (allVisibleSelected) {
      setSelectedMailIds((prev) => prev.filter((id) => !visibleKeys.includes(id)));
    } else {
      setSelectedMailIds(visibleKeys);
    }
  };

  const handleDeselectAll = () => {
    setSelectedMailIds([]);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMailIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      // Only delete selected mails that belong to the active category tab.
      const selectedMails = mails.filter((mail) => {
        const key = getMailKey(mail);
        if (!selectedMailIds.includes(key)) return false;
        if (showCategoryTabs && getMailCategory(mail) !== activeCategory) return false;
        return true;
      });
      const keysToRemove = new Set(selectedMails.map(getMailKey));
      const deletePromises = selectedMails.map((mail) => {
        if (isImapMail(mail)) {
          const uid = getMailUid(mail);
          if (!uid) return Promise.reject(new Error('Missing IMAP message uid'));
          return axiosInstance.patch(`/api/mail/trash/${uid}`);
        }
        const localId = getLocalMailId(mail);
        return axiosInstance.patch(`/api/mail/move-to-trash/${localId}`);
      });
      await Promise.all(deletePromises);

      setMails(mails.filter((m) => !keysToRemove.has(getMailKey(m))));
      if (selectedMail && keysToRemove.has(getMailKey(selectedMail))) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error deleting mails:', error);
      setAlertMessage('Error deleting mails. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkMarkUnread = async () => {
    if (selectedMailIds.length === 0) return;

    const selectedMails = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));

    // Optimistic UI update
    setMails(mails.map((m) =>
      selectedMailIds.includes(getMailKey(m)) ? { ...m, seen: false, isRead: false } : m
    ));
    setSelectedMailIds([]);

    try {
      await Promise.all(
        selectedMails.map((mail) => {
          if (isImapMail(mail)) {
            const uid = getMailUid(mail);
            if (!uid) return Promise.resolve();
            return axiosInstance.patch(`/api/mail/server/unread/${uid}`);
          }
          const localId = getLocalMailId(mail);
          return axiosInstance.patch(`/api/mail/mark-unread/${localId}`);
        })
      );
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error marking mails as unread:', error);
      setAlertMessage('Error marking mails as unread. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkMarkSpam = async () => {
    const inActiveTab = (mail) => !showCategoryTabs || getMailCategory(mail) === activeCategory;
    const targets = selectedMailIds.length
      ? mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)) && inActiveTab(mail))
      : selectedMail
        ? [selectedMail]
        : [];
    if (targets.length === 0) return;

    try {
      const { moved, failed } = await runMailMoves(targets, reportSpamRequest);
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not report spam. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error reporting spam:', error);
      setAlertMessage('Could not report spam. Please try again.');
      setShowAlert(true);
    }
  };

  const handleMoveToFolder = async (destination) => {
    const inActiveTab = (mail) => !showCategoryTabs || getMailCategory(mail) === activeCategory;
    const targets = selectedMailIds.length
      ? mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)) && inActiveTab(mail))
      : selectedMail
        ? [selectedMail]
        : [];
    if (!targets.length) return;

    try {
      const { moved, failed } = await runMailMoves(
        targets,
        (mail) => moveMailRequest(mail, destination, isSnoozedView ? 'snoozed' : 'inbox')
      );
      if (moved.length) {
        const movedKeys = new Set(moved.map(getMailKey));
        setMails((prev) => prev.filter((mail) => !movedKeys.has(getMailKey(mail))));
        setSelectedMailIds((prev) => prev.filter((id) => !movedKeys.has(id)));
        if (selectedMail && movedKeys.has(getMailKey(selectedMail))) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not move mail. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error moving mail:', error);
      setAlertMessage('Could not move mail. Please try again.');
      setShowAlert(true);
    }
  };

  const getSelectedMailsForLabels = () => {
    if (selectedMailIds.length > 0) {
      return mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    }
    if (!selectedMail) return [];

    // Prefer an IMAP/list mail from this conversation so label storage keys stay stable.
    const fromList = mails.find((mail) =>
      getMailKey(mail) === getMailKey(selectedMail) ||
      (selectedMail.messageId && mail.messageId && selectedMail.messageId === mail.messageId)
    );
    const fromThread = conversationMessages.find((msg) => isImapMail(msg)) || null;
    return [fromList || fromThread || selectedMail];
  };

  const refreshMailLabelsLocally = (targets, label, shouldHave) => {
    const keys = new Set(targets.map(getMailKey));
    const applyLabelList = (current = []) => (
      shouldHave
        ? [...current.filter((entry) => entry._id !== label._id), label]
        : current.filter((entry) => entry._id !== label._id)
    );

    setMails((prev) =>
      prev.map((mail) => {
        if (!keys.has(getMailKey(mail))) return mail;
        return { ...mail, labels: applyLabelList(mail.labels) };
      })
    );

    // Always reflect label chips on the open reading pane.
    if (selectedMail) {
      setSelectedMail((prev) => {
        if (!prev) return prev;
        return { ...prev, labels: applyLabelList(prev.labels) };
      });
    }
  };

  const handleToggleLabelForSelection = async (label, shouldApply) => {
    const targets = getSelectedMailsForLabels();
    if (!targets.length || labelMenuBusy) return;
    setLabelMenuBusy(true);
    refreshMailLabelsLocally(targets, label, shouldApply);
    try {
      const identities = targets.map((mail) => buildMailIdentity(mail));
      if (identities.length === 1) {
        if (shouldApply) {
          await assignLabelToMail(label._id, identities[0]);
        } else {
          await unassignLabelFromMail(label._id, identities[0]);
        }
      } else if (shouldApply) {
        await bulkAddLabel(label._id, identities);
      } else {
        await bulkRemoveLabel(label._id, identities);
      }
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
    } catch (error) {
      console.error('Error updating labels:', error);
      setAlertMessage(error.response?.data?.message || 'Could not update labels');
      setShowAlert(true);
      fetchInbox(false);
    } finally {
      setLabelMenuBusy(false);
    }
  };

  const selectedLabelIds = (() => {
    const targets = getSelectedMailsForLabels();
    if (!targets.length) return [];
    const counts = {};
    targets.forEach((mail) => {
      (mail.labels || []).forEach((label) => {
        counts[label._id] = (counts[label._id] || 0) + 1;
      });
    });
    return Object.keys(counts).filter((id) => counts[id] === targets.length);
  })();

  const handleBulkDownload = () => {
    if (selectedMailIds.length === 0) return;
    const selectedMails = mails.filter(m => selectedMailIds.includes(getMailKey(m)));
    selectedMails.forEach(mail => {
      handleDownloadMail({ stopPropagation: () => {} }, mail);
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if the message is from today
    const isToday = date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
      // Show time in "HH:MM AM/PM" format for today's messages
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      // Show date in "Mon DD" format for older messages
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDetailDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (diffMins < 1) return `${timeStr} (just now)`;
    if (diffMins < 60) return `${timeStr} (${diffMins} minute${diffMins === 1 ? '' : 's'} ago)`;
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    if (isToday) return timeStr;
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleSelectMail = (e, mailKey) => {
    e.stopPropagation();
    setSelectedMailIds((prev) =>
      prev.includes(mailKey) ? prev.filter((id) => id !== mailKey) : [...prev, mailKey]
    );
  };

  const handleListPageNewer = () => {
    if (canGoNewer) setListPage((p) => Math.max(0, p - 1));
  };

  const handleListPageOlder = () => {
    if (canGoOlder) setListPage((p) => Math.min(maxListPage, p + 1));
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading inbox...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchInbox} className="retry-button">Retry</button>
      </div>
    );
  }

  // Component to display authenticated images
  const AuthImage = ({ url, alt }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await fetch(url, {
            headers: getAttachmentAuthHeaders(),
          });
          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageSrc(objectUrl);
          } else {
            setImageError(true);
          }
        } catch (error) {
          console.error('Error loading image:', error);
          setImageError(true);
        }
      };

      loadImage();

      // Cleanup function to revoke object URL
      return () => {
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc);
        }
      };
    }, [url]);

    if (imageError) {
      return (
        <div className="image-load-error" style={{display: 'flex'}}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
          <p>Unable to load image</p>
        </div>
      );
    }

    if (!imageSrc) {
      return (
        <div style={{padding: '40px', textAlign: 'center', color: '#5f6368'}}>
          <p>Loading image...</p>
        </div>
      );
    }

    return <img src={imageSrc} alt={alt} style={{width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain', display: 'block'}} />;
  };

  return (
    <div className={`inbox-container premium-mail-dashboard reading-pane-${settings.readingPane} inbox-type-${settings.inboxType} ${selectedMail ? 'mail-detail-open' : ''} ${mobileSelectionActive ? 'mobile-selection-mode' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Inbox</h2>
        </div>
        <div className="inbox-header-actions">
          <span className="feature-chip">Calendar synced</span>
          <span className="feature-chip">Confidential mode</span>
          {mails.filter(m => !isMailRead(m)).length > 0 && (
            <p className="mail-count">{mails.filter(m => !isMailRead(m)).length} unread</p>
          )}
        </div>
      </div>
      {/* Mobile Compose Button */}
      <Link to="/compose" className={`mobile-compose-btn ${mobileSelectionActive ? 'hidden' : ''}`} title="Compose">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
        </svg>
        <span>Compose</span>
      </Link>
      <div
        className={[
          'inbox-content',
          `reading-pane-${readingPane}`,
          useSplitLayout ? 'gmail-split-layout' : '',
          hasDetailOpen ? 'has-detail-open' : '',
          fullDetailView ? 'full-detail-view' : '',
          selectedMail ? 'mobile-detail-open' : '',
        ].filter(Boolean).join(' ')}
      >
        <div
          className={[
            'mail-list-container',
            hideMailList ? 'hidden' : '',
          ].filter(Boolean).join(' ')}
        >
          {mobileSelectionActive && (
            <div className="mobile-selection-bar">
              <button type="button" className="mobile-selection-back" onClick={handleDeselectAll} aria-label="Cancel selection">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                </svg>
                <span>{selectedMailIds.length}</span>
              </button>
              <div className="mobile-selection-actions">
                <button type="button" className="mobile-selection-action" onClick={handleBulkArchive} title="Archive" aria-label="Archive">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
                  </svg>
                </button>
                <button type="button" className="mobile-selection-action" onClick={handleBulkDelete} title="Delete" aria-label="Delete">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
                <button type="button" className="mobile-selection-action" onClick={handleBulkMarkUnread} title="Mark as unread" aria-label="Mark as unread">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </button>
                <button type="button" className="mobile-selection-action" title="More options" aria-label="More options">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2"/>
                    <circle cx="12" cy="12" r="2"/>
                    <circle cx="12" cy="19" r="2"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {isMobileView && !selectedMail && !mobileSelectionActive && (
            <div className="mobile-inbox-title">Inbox</div>
          )}

          {mobileSelectionActive && (
            <button type="button" className="mobile-select-all-row" onClick={handleMobileSelectAll}>
              <span className={`mobile-select-all-check ${pageMails.every((m) => selectedMailIds.includes(getMailKey(m))) ? 'checked' : ''}`} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </span>
              Select all
            </button>
          )}

          {showCategoryTabs && visibleCategoryTabs.length > 0 && !mobileSelectionActive && (
            <div className="mail-category-tabs" role="tablist" aria-label="Inbox categories">
              {visibleCategoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === tab.id}
                  className={`mail-tab category-${tab.id} ${activeCategory === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(tab.id)}
                >
                  <svg className="mail-tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={tab.icon} />
                  </svg>
                  <span className="mail-tab-label">{tab.label}</span>
                  {categoryUnreadCounts[tab.id] > 0 && (
                    <span className="mail-tab-badge">{categoryUnreadCounts[tab.id]}</span>
                  )}
                </button>
              ))}
            </div>
          )}
          {displayedMails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button 
                  className="toolbar-btn checkbox-btn"
                  onClick={selectedMailIds.length > 0 ? handleDeselectAll : handleSelectAll}
                  title={selectedMailIds.length > 0 ? "Deselect all" : "Select all"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    {selectedMailIds.length > 0 && (
                      <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                    )}
                  </svg>
                  {selectedMailIds.length > 0 && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={fetchInbox}
                  title="Refresh"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                
                {selectedMailIds.length > 0 && (
                  <>
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDownload}
                      title="Download"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkSpam}
                      title="Report spam"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDelete}
                      title="Delete"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              
              {selectedMailIds.length > 0 && (
                <>
                  <div className="toolbar-separator"></div>
                  <div className="toolbar-right">
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkUnread}
                      title="Mark as unread"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <circle cx="18" cy="4" r="3" fill="currentColor" className="notification-dot"/>
                    </button>
                    
                    <MoveToFolderButton
                      currentFolder={isSnoozedView ? 'snoozed' : 'inbox'}
                      onMove={handleMoveToFolder}
                    />

                    <div className="toolbar-label-menu-wrap" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="toolbar-btn"
                        title="Labels"
                        aria-haspopup="menu"
                        aria-expanded={showLabelMenu}
                        onClick={() => {
                          setShowLabelMenu((open) => !open);
                          loadUserLabels();
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z" />
                        </svg>
                      </button>
                      {showLabelMenu && (
                        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50 }}>
                          <LabelSelector
                            labels={userLabels}
                            selectedIds={selectedLabelIds}
                            onToggle={handleToggleLabelForSelection}
                            onCreate={() => {
                              setShowLabelMenu(false);
                              setShowCreateLabelModal(true);
                            }}
                          />
                        </div>
                      )}
                    </div>
                    
                    <button 
                      className="toolbar-btn"
                      title="More options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {totalMailCount > 0 && (
            <div className="mail-list-meta">
              <span className="mail-list-meta-text">
                {visibleStart}–{visibleEnd} of {totalMailCount}
              </span>
              <button
                type="button"
                className="mail-list-meta-btn"
                onClick={handleListPageNewer}
                disabled={!canGoNewer}
                aria-label="Newer emails"
                title="Newer"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <button
                type="button"
                className="mail-list-meta-btn"
                onClick={handleListPageOlder}
                disabled={!canGoOlder}
                aria-label="Older emails"
                title="Older"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          )}
          <div className="mail-list">
            {totalMailCount === 0 ? (
              <div className="empty-inbox">
                {showCategoryTabs && mails.length > 0 && activeCategory !== 'primary' ? (
                  <>
                    <p>No mail in {activeCategory}</p>
                    <p className="empty-hint">Nothing to see in this category right now.</p>
                  </>
                ) : (
                  <>
                    <p>Your inbox is empty</p>
                    <p className="empty-hint">Compose a mail to get started!</p>
                  </>
                )}
              </div>
            ) : (
              pageMails.map((mail) => {
                const mailKey = getMailKey(mail);
                const preview = getMailPreview(mail);
                const isSelected = selectedMailIds.includes(mailKey);
                const avatarColors = getAvatarColor(mail.from);
                const hasAttachments = Boolean(mail.attachments && mail.attachments.length > 0);
                return (
                <div
                  key={mailKey}
                  role="button"
                  tabIndex={0}
                  className={`mail-item gmail-split-row ${!isMailRead(mail) ? 'unread' : 'read'} ${selectedMail && getMailKey(selectedMail) === mailKey ? 'selected' : ''} ${isSelected ? 'mail-selected mobile-row-selected' : ''} ${hasAttachments ? 'has-attachments' : ''} ${hoveredContactKey === mailKey ? 'contact-card-open' : ''}`}
                  onClick={() => handleMailRowClick(mail)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMailRowClick(mail); } }}
                  onTouchStart={() => startLongPress(mailKey)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  onContextMenu={(e) => {
                    if (isMobileView) {
                      e.preventDefault();
                      longPressTriggeredRef.current = true;
                      toggleMailSelection(mailKey);
                    }
                  }}
                >
                  <span className="gmail-row-drag-handle" aria-hidden="true">
                    <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                      <circle cx="3" cy="4" r="1.5" />
                      <circle cx="9" cy="4" r="1.5" />
                      <circle cx="3" cy="10" r="1.5" />
                      <circle cx="9" cy="10" r="1.5" />
                      <circle cx="3" cy="16" r="1.5" />
                      <circle cx="9" cy="16" r="1.5" />
                    </svg>
                  </span>
                  <input type="checkbox" className="mail-checkbox gmail-col-check" checked={isSelected} onChange={(e) => handleSelectMail(e, mailKey)} onClick={(e) => e.stopPropagation()} aria-label="Select mail" />
                  <button
                    type="button"
                    className={`mail-star-btn gmail-row-star gmail-row-star-left ${mail.isStarred ? 'starred' : ''}`}
                    onClick={(e) => handleToggleStar(e, mail)}
                    title={mail.isStarred ? 'Unstar' : 'Star'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      {mail.isStarred ? (
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      ) : (
                        <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                      )}
                    </svg>
                  </button>
                  <span className={`mail-read-dot ${!isMailRead(mail) ? 'visible' : ''}`} aria-hidden="true" />
                  <div
                    className={`mail-sender-avatar ${isSelected ? 'avatar-selected' : ''}`}
                    style={!isSelected ? { backgroundColor: avatarColors.bg, color: avatarColors.color } : undefined}
                    aria-hidden="true"
                  >
                    {isSelected ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    ) : (
                      getSenderInitial(mail.from)
                    )}
                  </div>
                  <div className="gmail-row-body">
                    <div className="gmail-row-primary">
                      <span
                        className="mail-sender-hover-wrap"
                        onMouseEnter={() => {
                          setHoveredContactKey(mailKey);
                          fetchSenderProfile(mail);
                        }}
                        onMouseLeave={() => setHoveredContactKey('')}
                      >
                        <span className="mail-sender">{parseSenderDisplay(mail.from)}</span>
                        {renderSenderHoverCard(mail)}
                      </span>
                      <div className="gmail-row-main-line">
                        <div className="gmail-row-subject">
                          <span className="gmail-row-subject-text">{mail.subject || '(no subject)'}</span>
                          {mail.threadCount > 1 ? (
                            <span className="mail-thread-count">{mail.threadCount}</span>
                          ) : null}
                          {(mail.labels || []).slice(0, 3).map((label) => (
                            <LabelBadge key={label._id} label={label} />
                          ))}
                        </div>
                        {preview && !isMobileView ? <span className="gmail-row-snippet">{preview}</span> : null}
                      </div>
                    </div>
                    {!isMobileView && hasAttachments && (
                      <div className="gmail-row-attachments-line">
                        <div className="mail-attachment-chips">
                          {mail.attachments.slice(0, 3).map((att, i) => {
                            const chipType = getAttachmentChipType(att);
                            return (
                              <span
                                key={i}
                                className={`mail-attachment-chip mail-attachment-chip-${chipType}`}
                                title={att.filename}
                              >
                                <span className="mail-attachment-chip-icon" aria-hidden="true">
                                  {renderAttachmentChipIcon(chipType)}
                                </span>
                                <span className="mail-attachment-chip-name">
                                  {truncateAttachmentName(att.filename || 'attachment', 18)}
                                </span>
                              </span>
                            );
                          })}
                          {mail.attachments.length > 3 && (
                            <span className="mail-attachment-chip mail-attachment-chip-more">
                              +{mail.attachments.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="gmail-row-aside">
                    <span className="mail-date">{formatDate(mail.createdAt || mail.date)}</span>
                    {!isMobileView && (
                      <div className="gmail-row-hover-actions">
                        <button type="button" className="mail-hover-btn" title="Archive" aria-label="Archive" onClick={(e) => handleArchiveMail(e, mail)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z" />
                          </svg>
                        </button>
                        <button type="button" className="mail-hover-btn" title="Delete" aria-label="Delete" onClick={(e) => handleDeleteMail(e, mailKey)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        </button>
                        <button type="button" className="mail-hover-btn" title={isMailRead(mail) ? 'Mark as unread' : 'Mark as read'} aria-label={isMailRead(mail) ? 'Mark as unread' : 'Mark as read'} onClick={(e) => handleMarkUnread(e, mail)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                          </svg>
                        </button>
                        <button type="button" className="mail-hover-btn" title="Snooze" aria-label="Snooze" onClick={(e) => handleSnoozeMail(e, mail)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <button type="button" className={`mail-star-btn gmail-row-star gmail-row-star-aside ${mail.isStarred ? 'starred' : ''}`} onClick={(e) => handleToggleStar(e, mail)} title={mail.isStarred ? 'Unstar' : 'Star'}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        {mail.isStarred ? (
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        ) : (
                          <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
        {selectedMail && (
          <div className="mail-detail-container">
            <div className="mail-detail-header">
              <button onClick={handleCloseMail} className="back-button" aria-label="Back">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                </svg>
              </button>
              <div className="header-subject-section">
                <h3 className="header-subject">{selectedMail.subject || '(No subject)'}</h3>
                {selectedMail.isImportant && (
                  <span className="folder-label label-important">Important</span>
                )}
                <span className="folder-label">Inbox</span>
                {(selectedMail.labels || []).map((label) => (
                  <LabelBadge
                    key={label._id}
                    label={label}
                    removable
                    onRemove={() => handleToggleLabelForSelection(label, false)}
                  />
                ))}
                <div className="toolbar-label-menu-wrap" style={{ position: 'relative', display: 'inline-flex' }}>
                  <button
                    type="button"
                    className="header-action-btn"
                    title="Labels"
                    onClick={() => {
                      setShowLabelMenu((open) => !open);
                      loadUserLabels();
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z" />
                    </svg>
                  </button>
                  {showLabelMenu && selectedMailIds.length === 0 && (
                    <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50 }}>
                      <LabelSelector
                        labels={userLabels}
                        selectedIds={(selectedMail.labels || []).map((label) => String(label._id))}
                        onToggle={handleToggleLabelForSelection}
                        onCreate={() => {
                          setShowLabelMenu(false);
                          setShowCreateLabelModal(true);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="header-actions-right">
                <button 
                  className="header-action-btn" 
                  onClick={(e) => handleDeleteMail(e, getMailKey(selectedMail))}
                  title="Archive"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
                  </svg>
                </button>
                <button 
                  className="header-action-btn" 
                  onClick={handleBulkMarkSpam}
                  title="Report spam"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </button>
                <button 
                  className="header-action-btn" 
                  onClick={(e) => handleDeleteMail(e, getMailKey(selectedMail))}
                  title="Delete"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
                <button 
                  className="header-action-btn" 
                  onClick={(e) => handleMarkUnread(e, selectedMail)}
                  title={isMailRead(selectedMail) ? "Mark as unread" : "Mark as read"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </button>
                <MoveToFolderButton
                  className="header-action-btn"
                  variant="header"
                  currentFolder={isSnoozedView ? 'snoozed' : 'inbox'}
                  onMove={handleMoveToFolder}
                />
                <div className="msg-more-menu-wrap detail-more-menu-wrap">
                  <button
                    type="button"
                    className="header-action-btn detail-more-btn"
                    title="More options"
                    aria-label="More options"
                    aria-expanded={detailMoreMenuOpen}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailMoreMenuOpen((open) => !open);
                      setMessageMoreMenuKey(null);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                  {detailMoreMenuOpen && selectedMail && renderMessageMoreMenu(selectedMail)}
                </div>
              </div>
            </div>
            <div className="mail-detail-scroll" ref={mailDetailScrollRef}>
              <div className="mail-detail-content mail-detail-card gmail-thread-panel">
                <div className="gmail-conversation-thread">
                {conversationMessages.length === 0 ? (
                  <div className="mail-conversation-empty">No messages in this conversation.</div>
                ) : (
                  conversationMessages.map((msg, index) => {
                    const isExpanded =
                      expandedThreadIndex === null
                        ? index === conversationMessages.length - 1
                        : index === expandedThreadIndex;
                    const isOwn = isOwnConversationMessage(msg);
                    const msgKey = getMailDedupeKey(msg);
                    const htmlContent = getMailHtmlContent(msg);
                    const preview = msg.replyText || getMailPreview(msg);
                    const plainContent = String(msg.body || msg.text || '').trim();
                    const messageParts = splitMessageForDisplay(msg);
                    const showStructuredReply = messageParts.hasQuote;
                    const hasMessageAttachments = Boolean(msg.attachments && msg.attachments.length > 0);

                    return (
                      <div
                        key={msgKey}
                        className={`gmail-thread-row ${isExpanded ? 'is-expanded' : 'is-collapsed'}`}
                      >
                        <div className="gmail-thread-row-top">
                          <div
                            className="gmail-thread-row-hit"
                            onClick={() => !isExpanded && handleExpandThreadMessage(msg, index)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? undefined : `Show message from ${getSenderName(msg.from)}`}
                            role="button"
                            tabIndex={isExpanded ? -1 : 0}
                            onKeyDown={(event) => {
                              if (!isExpanded && (event.key === 'Enter' || event.key === ' ')) {
                                event.preventDefault();
                                handleExpandThreadMessage(msg, index);
                              }
                            }}
                          >
                            <span
                              className="gmail-thread-avatar"
                              style={{ backgroundColor: isOwn ? '#8e63ce' : '#1a73e8', color: '#fff' }}
                            >
                              {getSenderInitial(msg.from)}
                            </span>
                            {!isExpanded ? (
                              <span className="gmail-thread-inline">
                                <span className="gmail-thread-sender">
                                  {isOwn ? 'me' : getSenderName(msg.from)}
                                </span>
                                <span className="gmail-thread-snippet">{preview}</span>
                              </span>
                            ) : (
                              <span className="gmail-thread-expanded-head">
                                <span className="gmail-thread-sender-block">
                                  <span className="gmail-thread-sender">
                                    {isOwn ? 'me' : getSenderName(msg.from)}
                                  </span>
                                  <span
                                    className="recipient-info gmail-thread-recipient-info"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setShowHeaderDetails((openKey) => openKey === msgKey ? false : msgKey);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setShowHeaderDetails((openKey) => openKey === msgKey ? false : msgKey);
                                      }
                                    }}
                                  >
                                    <span className="gmail-thread-recipient">{getRecipientLabel(msg, isOwn)}</span>
                                    <svg
                                      className={`dropdown-arrow ${showHeaderDetails === msgKey ? 'open' : ''}`}
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path d="M7 10l5 5 5-5z" />
                                    </svg>
                                  </span>
                                  {showHeaderDetails === msgKey && renderHeaderDetailsPopup(msg)}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className="gmail-thread-meta">
                            <span className="gmail-thread-time">{formatDetailDate(msg.createdAt || msg.date)}</span>
                            <button
                              type="button"
                              className={`gmail-thread-star ${msg.isStarred ? 'starred' : ''}`}
                              onClick={(e) => handleToggleStar(e, msg)}
                              title={msg.isStarred ? 'Unstar' : 'Star'}
                              aria-label={msg.isStarred ? 'Unstar' : 'Star'}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                {msg.isStarred ? (
                                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                ) : (
                                  <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                                )}
                              </svg>
                            </button>
                            {isExpanded && (
                              <div className="gmail-thread-msg-actions">
                                <div className="reaction-wrapper">
                                  <button
                                    type="button"
                                    className="header-action-btn msg-action-btn"
                                    onClick={(e) => { e.stopPropagation(); handleAddReaction(getMailKey(msg)); }}
                                    title="Add reaction"
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                      <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                                      <circle cx="15.5" cy="10.5" r="1.5" fill="currentColor" />
                                      <path d="M12 18c2.28 0 4.22-1.66 5-4H7c.78 2.34 2.72 4 5 4z" fill="currentColor" />
                                    </svg>
                                  </button>
                                  {reactionPickerKey === getMailKey(msg) && (
                                    <div className="reaction-picker" onClick={(e) => e.stopPropagation()}>
                                      {REACTION_EMOJIS.map((emoji) => (
                                        <button
                                          type="button"
                                          key={emoji}
                                          className={`reaction-picker-emoji ${getMessageReactions(getMailKey(msg)).includes(emoji) ? 'selected' : ''}`}
                                          onClick={() => handlePickReaction(getMailKey(msg), emoji)}
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button type="button" className="header-action-btn msg-action-btn" onClick={(e) => { e.stopPropagation(); handleReply(msg); }} title="Reply">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
                                  </svg>
                                </button>
                                <div className="msg-more-menu-wrap">
                                  <button
                                    type="button"
                                    className="header-action-btn msg-action-btn msg-more-btn"
                                    title="More"
                                    aria-label="More options"
                                    aria-expanded={messageMoreMenuKey === getMailKey(msg)}
                                    onClick={(e) => toggleMessageMoreMenu(e, msg)}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                      <circle cx="12" cy="5" r="2" />
                                      <circle cx="12" cy="12" r="2" />
                                      <circle cx="12" cy="19" r="2" />
                                    </svg>
                                  </button>
                                  {messageMoreMenuKey === getMailKey(msg) && renderMessageMoreMenu(msg)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="gmail-thread-expanded-body">
                            <div className="mail-body gmail-thread-body">
                              {showStructuredReply ? (
                                <>
                                  {messageParts.replyPart && (
                                    <div
                                      className="body-content gmail-thread-reply-text"
                                      style={{ whiteSpace: 'pre-wrap' }}
                                      dangerouslySetInnerHTML={{
                                        __html: linkifyPlainText(messageParts.replyPart),
                                      }}
                                    />
                                  )}
                                  {messageParts.quoteHeader && (
                                    <div className="gmail-thread-quote-block">
                                      <div className="gmail-thread-quote-header">{messageParts.quoteHeader}</div>
                                      {messageParts.quoteBody && (
                                        <div
                                          className="gmail-thread-quote-body"
                                          style={{ whiteSpace: 'pre-wrap' }}
                                          dangerouslySetInnerHTML={{
                                            __html: linkifyPlainText(messageParts.quoteBody),
                                          }}
                                        />
                                      )}
                                    </div>
                                  )}
                                </>
                              ) : htmlContent ? (
                                <div className="mail-html-sandbox">
                                  <div className="body-content mail-html-body" dangerouslySetInnerHTML={{ __html: htmlContent }} />
                                </div>
                              ) : plainContent ? (
                                <div
                                  className="body-content"
                                  style={{ whiteSpace: 'pre-wrap' }}
                                  dangerouslySetInnerHTML={{ __html: linkifyPlainText(plainContent) }}
                                />
                              ) : hasMessageAttachments ? (
                                <div className="mail-empty-body-note">
                                  This message has no text body. Open or download the attachment below to view the report.
                                </div>
                              ) : (
                                <div className="mail-empty-body-note">No message content to display.</div>
                              )}
                            </div>

                            {getMessageReactions(getMailKey(msg)).length > 0 && (
                              <div className="mail-reactions-bar">
                                {getMessageReactions(getMailKey(msg)).map((emoji) => (
                                  <button
                                    type="button"
                                    key={emoji}
                                    className="mail-reaction-chip active"
                                    onClick={() => toggleMessageReaction(getMailKey(msg), emoji)}
                                    title="Remove reaction"
                                  >
                                    <span className="mail-reaction-emoji">{emoji}</span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {hasMessageAttachments && (
                              <div className="mail-attachments-section gmail-attachments-section">
                                <div className="gmail-attachments-toolbar">
                                  <div className="gmail-attachments-meta">
                                    <span>{getAttachmentCountLabel(msg.attachments.length)}</span>
                                    <span className="gmail-attachments-dot" aria-hidden="true">•</span>
                                    <span>Scanned by ATOZASMAIL</span>
                                    <button type="button" className="gmail-attachments-info" title="Attachment info" aria-label="Attachment info">
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                <div className="attachments-list gmail-attachments-list">
                                  {msg.attachments.map((attachment, attachIndex) => {
                                    const isImage = isImageAttachment(attachment);
                                    const attachmentUrl = getAttachmentUrl(msg, attachIndex);
                                    if (isImage) {
                                      return (
                                        <div key={attachIndex} className="gmail-attachment-image-wrap">
                                          <div className="gmail-attachment-image-card">
                                            <div className="gmail-attachment-filename-tooltip" aria-hidden="true">
                                              {attachment.filename || 'image'}
                                            </div>
                                            <div className="gmail-attachment-thumb">
                                              <AuthImage url={attachmentUrl} alt={attachment.filename} />
                                            </div>
                                            <div className="gmail-attachment-hover-overlay">
                                              <div className="gmail-attachment-hover-info">
                                                <span className="gmail-attachment-file-icon" aria-hidden="true">
                                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                  </svg>
                                                </span>
                                                <div className="gmail-attachment-hover-text">
                                                  <span className="gmail-attachment-hover-name">
                                                    {truncateAttachmentName(attachment.filename)}
                                                  </span>
                                                  <span className="gmail-attachment-hover-size">
                                                    {formatAttachmentSize(attachment.size)}
                                                  </span>
                                                </div>
                                              </div>
                                              <div className="gmail-attachment-hover-actions">
                                                <button
                                                  type="button"
                                                  className="gmail-attachment-action-btn"
                                                  title="Download"
                                                  aria-label="Download attachment"
                                                  onClick={() => downloadAttachmentFile(msg, attachIndex, attachment.filename)}
                                                >
                                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                                                  </svg>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="gmail-attachment-action-btn"
                                                  title="Add to Drive"
                                                  aria-label="Add to Drive"
                                                  disabled
                                                >
                                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M7.71 3.5 1.15 15l3.42 5.91L12 12.09 7.71 3.5zm8.58 0L12 12.09l7.43 8.82L23.85 15 16.29 3.5zM12 13.91 4.57 22h14.86L12 13.91z" />
                                                  </svg>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="gmail-attachment-action-btn"
                                                  title="Open"
                                                  aria-label="Open attachment"
                                                  onClick={() => openAttachmentFile(msg, attachIndex)}
                                                >
                                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                                                  </svg>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div key={attachIndex} className="attachment-container-detail">
                                        <div className="attachment-item-detail">
                                          <div className="attachment-icon-wrapper">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                              <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
                                            </svg>
                                          </div>
                                          <div className="attachment-info-detail">
                                            <div className="attachment-name-detail">{attachment.filename}</div>
                                            <div className="attachment-size-detail">{formatAttachmentSize(attachment.size)}</div>
                                          </div>
                                          <button
                                            type="button"
                                            className="attachment-download-btn"
                                            onClick={() => downloadAttachmentFile(msg, attachIndex, attachment.filename)}
                                            title="Download"
                                          >
                                            Download
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

              {showReplyBox && (
                <div className="gmail-inline-reply">
                  <div className="gmail-inline-reply-to">
                    to {parseSenderDisplay(getReplyRecipientEmail() || getReplyTargetMail()?.from)}
                  </div>
                  <div className="gmail-inline-reply-row">
                    <span
                      className="gmail-thread-avatar gmail-inline-reply-avatar"
                      style={{ backgroundColor: '#8e63ce', color: '#fff' }}
                    >
                      {getSenderInitial(currentUser?.email)}
                    </span>
                    <div className="gmail-inline-reply-editor">
                      <textarea
                        className="gmail-inline-reply-textarea"
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        placeholder=""
                        rows={3}
                        disabled={isSendingReply}
                        autoFocus
                      />
                      <div className="gmail-inline-reply-quote-line">
                        {getQuotedContent().header}
                      </div>
                      {showQuotedContent && getQuotedContent().body && (
                        <div className="gmail-inline-reply-quoted">
                          {getQuotedContent().body}
                        </div>
                      )}
                      <button
                        type="button"
                        className="gmail-inline-reply-toggle"
                        onClick={() => setShowQuotedContent(!showQuotedContent)}
                        aria-label={showQuotedContent ? 'Hide quoted text' : 'Show quoted text'}
                        title={showQuotedContent ? 'Hide quoted text' : 'Show quoted text'}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="5" cy="12" r="1.75" />
                          <circle cx="12" cy="12" r="1.75" />
                          <circle cx="19" cy="12" r="1.75" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="gmail-inline-reply-toolbar">
                    <button
                      type="button"
                      className="gmail-send-reply-btn"
                      onClick={handleSendReply}
                      disabled={isSendingReply || !replyBody.trim()}
                    >
                      {isSendingReply ? 'Sending...' : 'Send'}
                    </button>
                    <button
                      type="button"
                      className="gmail-discard-reply-btn"
                      onClick={handleCancelReply}
                      disabled={isSendingReply}
                      aria-label="Discard reply"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <div ref={threadEndRef} className="gmail-thread-anchor" aria-hidden="true" />
              </div>
            </div>

            {selectedMail && getMessageReactions(getMailKey(selectedMail)).length > 0 && (
              <div className="mail-reactions-bar">
                {getMessageReactions(getMailKey(selectedMail)).map((emoji) => (
                  <button
                    type="button"
                    key={emoji}
                    className="mail-reaction-chip active"
                    onClick={() => toggleMessageReaction(getMailKey(selectedMail), emoji)}
                    title="Remove reaction"
                  >
                    <span className="mail-reaction-emoji">{emoji}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="mail-action-buttons gmail-thread-footer">
                <button type="button" className="reply-btn" onClick={(e) => { e.stopPropagation(); handleReply(); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                  </svg>
                  <span>Reply</span>
                </button>
                <button type="button" className="reply-btn reply-all-btn" onClick={(e) => { e.stopPropagation(); handleReplyAll(); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 7v4H2l7 7 7-7h-5V7H7zm10 0h-4v2h4v6h-3l5 5 5-5h-3V7h-4z"/>
                  </svg>
                  <span>Reply all</span>
                </button>
                <button type="button" className="forward-btn" onClick={(e) => { e.stopPropagation(); handleForward(); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 8V4l8 8-8 8v-4H4V8z"/>
                  </svg>
                  <span>Forward</span>
                </button>
                <MailWorkspaceActions mail={selectedMail} />
                <div className="reaction-wrapper">
                  <button
                    className="reaction-btn"
                    onClick={(e) => { e.stopPropagation(); handleAddReaction(getMailKey(selectedMail)); }}
                    title="Add reaction"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor"/>
                      <circle cx="15.5" cy="10.5" r="1.5" fill="currentColor"/>
                      <path d="M12 18c2.28 0 4.22-1.66 5-4H7c.78 2.34 2.72 4 5 4z" fill="currentColor"/>
                    </svg>
                  </button>
                  {selectedMail && reactionPickerKey === getMailKey(selectedMail) && (
                    <div className="reaction-picker" onClick={(e) => e.stopPropagation()}>
                      {REACTION_EMOJIS.map((emoji) => (
                        <button
                          type="button"
                          key={emoji}
                          className={`reaction-picker-emoji ${getMessageReactions(getMailKey(selectedMail)).includes(emoji) ? 'selected' : ''}`}
                          onClick={() => handlePickReaction(getMailKey(selectedMail), emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="emoji-reactions" aria-label="Quick reactions">
                  {['👍', '✅', '🔥'].map((emoji) => (
                    <button
                      type="button"
                      key={emoji}
                      className={getMessageReactions(getMailKey(selectedMail)).includes(emoji) ? 'active' : ''}
                      onClick={(e) => { e.stopPropagation(); toggleMessageReaction(getMailKey(selectedMail), emoji); }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
          </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMailToDelete(null);
        }}
        onConfirm={confirmDeleteMail}
        message="Are you sure you want to delete this mail?"
        confirmText="OK"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showDeleteReplyConfirm}
        onClose={() => {
          setShowDeleteReplyConfirm(false);
          setReplyToDelete(null);
        }}
        onConfirm={confirmDeleteReply}
        message="Are you sure you want to delete this reply?"
        confirmText="OK"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${selectedMailIds.length} mail(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />

      <CreateLabelModal
        isOpen={showCreateLabelModal}
        onClose={() => setShowCreateLabelModal(false)}
        onCreated={(label) => {
          setUserLabels((prev) => [...prev, label]);
          window.dispatchEvent(new CustomEvent('labelsUpdated'));
        }}
      />

      {snoozeMenu && (
        <>
          <div className="snooze-menu-backdrop" onClick={() => setSnoozeMenu(null)} />
          <div className="snooze-menu" style={{ top: snoozeMenu.y, left: snoozeMenu.x }}>
            <div className="snooze-menu-title">Snooze until…</div>
            {buildSnoozePresets().map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="snooze-menu-item"
                onClick={() => performSnooze(snoozeMenu.mail, preset.date)}
              >
                <span className="snooze-menu-label">{preset.label}</span>
                <span className="snooze-menu-sub">{preset.sub}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {selectedMail && (
        <AiAssistantPanel
          email={selectedMail}
          currentUser={currentUser}
          isImap={isImapMail(selectedMail)}
        />
      )}
    </div>
  );
};

export default Inbox;

